import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ProgressCalendar from './ProgressCalendar';
import StreakTracker from './StreakTracker';
import DailyJournal from './DailyJournal';
import BadgeDisplay, { BadgeDisplayLocked } from './BadgeDisplay';
import { getUserBadges, BADGE_DEFINITIONS } from '../utils/badgeEngine';
import './ProgressDashboard.css';
import logo from '../assets/logo.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { theme } from '../theme';
import { getActiveScrollId } from '../utils/progressTracker';

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Badge category display order and labels
const BADGE_CATEGORIES = [
  { key: 'streak',  label: 'Streaks',       description: 'Consecutive days of reading' },
  { key: 'perfect', label: 'Perfect Days',   description: 'Days with all three readings completed' },
  { key: 'days',    label: 'Total Days',     description: 'Total qualifying days across all scrolls' },
  { key: 'scroll',  label: 'Scroll Mastery', description: 'Completing 30 days with each scroll' },
  { key: 'journey', label: 'Journey',        description: 'The greatest milestones of all' },
];

const ProgressDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return ['overview', 'badges', 'journal'].includes(tabParam) ? tabParam : 'overview';
  });
  const [todayStats, setTodayStats] = useState({ readingsComplete: 0, journalWritten: false });
  const [earnedBadges, setEarnedBadges] = useState(new Set());
  const [badgeEarnedDates, setBadgeEarnedDates] = useState({});
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [activeScrollId, setActiveScrollId] = useState(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        fetchTodayStats(user.id);
        loadBadges();
        getActiveScrollId().then(setActiveScrollId);
      }
    };
    getCurrentUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchTodayStats(session.user.id);
        loadBadges();
        getActiveScrollId().then(setActiveScrollId);
      }
    });

    return () => { authListener?.subscription?.unsubscribe(); };
  }, []);

  const loadBadges = async () => {
    setBadgesLoading(true);
    const badges = await getUserBadges();
    const earnedSet = new Set(badges.map(b => b.badge_id));
    const dateMap = {};
    badges.forEach(b => { dateMap[b.badge_id] = b.earned_at; });
    setEarnedBadges(earnedSet);
    setBadgeEarnedDates(dateMap);
    setBadgesLoading(false);
  };

  const fetchTodayStats = async (userId) => {
    try {
      const today = getLocalDateString();
      const { data: readings } = await supabase
        .from('scroll_readings')
        .select('reading_number')
        .eq('user_id', userId)
        .eq('date', today);

      const { data: journal } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('date', today)
        .single();

      setTodayStats({
        readingsComplete: readings?.length || 0,
        journalWritten: !!journal,
      });
    } catch (error) {
      console.error('Error fetching today stats:', error);
    }
  };

  const getTodayProgress = () => Math.min((todayStats.readingsComplete / 3) * 100, 100);

  const tabs = [
    { id: 'overview',  label: 'Overview', icon: '📊' },
    { id: 'badges',    label: 'Badges',   icon: '🏆' },
    { id: 'journal',   label: 'Journal',  icon: '📝' },
  ];

  const totalEarned = earnedBadges.size;
  const totalBadges = Object.keys(BADGE_DEFINITIONS).length;

  if (!user) {
    return (
      <div className="progress-dashboard">
        <div className="not-authenticated">
          <h2>Please sign in to view your progress</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <img
            src={logo}
            alt="My Greatest Self"
            className="dashboard-logo clickable"
            onClick={() => navigate('/dashboard')}
            title="Back to Home"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="header-text">
            <h1>My Progress</h1>
            <p className="welcome-message">Keep building your greatest self.</p>
          </div>
          <div style={headerButtonGroup}>
            {activeScrollId && (
              <button
                onClick={() => navigate(`/scroll/${activeScrollId}`)}
                style={continueScrollButton}
              >
                Read Active Scroll →
              </button>
            )}
            <button onClick={() => navigate('/dashboard')} className="back-button">
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-tabs" style={{ justifyContent: 'flex-start' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            {tab.id === 'badges' && totalEarned > 0 && (
              <span style={badgeCountStyle}>{totalEarned}</span>
            )}
          </button>
        ))}
      </div>

      <div className="dashboard-content">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="today-card">
              <h2>Today's Progress</h2>
              <div className="today-stats">
                <div className="stat-item">
                  <div className="stat-icon">📖</div>
                  <div className="stat-content">
                    <div className="stat-value">{todayStats.readingsComplete}/3</div>
                    <div className="stat-label">Readings Complete</div>
                  </div>
                </div>
                <div className="stat-item clickable" onClick={() => setActiveTab('journal')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div className="stat-icon" style={{ cursor: 'pointer' }}>📝</div>
                  <div className="stat-content" style={{ cursor: 'pointer' }}>
                    <div className="stat-value" style={{ cursor: 'pointer', userSelect: 'none' }}>{todayStats.journalWritten ? 'Yes' : 'No'}</div>
                    <div className="stat-label" style={{ cursor: 'pointer', userSelect: 'none' }}>Journal Entry →</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-content">
                    <div className="stat-value">{totalEarned}/{totalBadges}</div>
                    <div className="stat-label">Badges Earned</div>
                  </div>
                </div>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${getTodayProgress()}%` }} />
                </div>
                <div className="progress-label">
                  {getTodayProgress() === 100 ? '🎉 Perfect Day!' : `${getTodayProgress().toFixed(0)}% Complete`}
                </div>
              </div>
            </div>

            {/* Recent badges earned */}
            {totalEarned > 0 && (
              <div style={recentBadgesSection}>
                <h3 style={recentBadgesTitle}>Recent Badges</h3>
                <div style={recentBadgesRow}>
                  {Array.from(earnedBadges)
                    .slice(-5)
                    .reverse()
                    .map(badgeId => {
                      const def = BADGE_DEFINITIONS[badgeId];
                      if (!def) return null;
                      return (
                        <div key={badgeId} style={recentBadgeItem} title={def.name}>
                          <BadgeDisplay badge={def} size={56} />
                          <span style={recentBadgeName}>{def.name}</span>
                        </div>
                      );
                    })}
                  {totalEarned > 5 && (
                    <button
                      style={seeAllButton}
                      onClick={() => setActiveTab('badges')}
                    >
                      See all {totalEarned} →
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="quick-stats-grid">
              <StreakTracker userId={user.id} />
            </div>
            <div className="recent-activity-section">
              <h3>Recent Activity</h3>
              <ProgressCalendar userId={user.id} />
            </div>
          </div>
        )}

        {/* ── BADGES ── */}
        {activeTab === 'badges' && (
          <div style={badgesTab}>
            <div style={badgesSummary}>
              <span style={badgesSummaryText}>
                {totalEarned} of {totalBadges} badges earned
              </span>
              <div style={badgesProgressBar}>
                <div
                  style={{
                    ...badgesProgressFill,
                    width: `${(totalEarned / totalBadges) * 100}%`,
                  }}
                />
              </div>
            </div>

            {badgesLoading ? (
              <p style={{ color: theme.colors.textSecondary, textAlign: 'center', padding: '40px' }}>
                Loading badges...
              </p>
            ) : (
              BADGE_CATEGORIES.map(category => {
                const categoryBadges = Object.values(BADGE_DEFINITIONS).filter(
                  b => b.category === category.key
                );
                const earnedInCategory = categoryBadges.filter(b => earnedBadges.has(b.id)).length;

                return (
                  <div key={category.key} style={categorySection}>
                    <div style={categoryHeader}>
                      <div>
                        <h3 style={categoryTitle}>{category.label}</h3>
                        <p style={categoryDesc}>{category.description}</p>
                      </div>
                      <span style={categoryCount}>
                        {earnedInCategory}/{categoryBadges.length}
                      </span>
                    </div>

                    <div style={badgeGrid}>
                      {categoryBadges.map(badge => {
                        const isEarned = earnedBadges.has(badge.id);
                        const earnedDate = badgeEarnedDates[badge.id];

                        return (
                          <div
                            key={badge.id}
                            style={{
                              ...badgeCard,
                              ...(isEarned ? badgeCardEarned(badge.color) : badgeCardLocked),
                            }}
                            title={isEarned ? `Earned ${new Date(earnedDate).toLocaleDateString()}` : 'Not yet earned'}
                          >
                            {isEarned
                              ? <BadgeDisplay badge={badge} size={72} />
                              : <BadgeDisplayLocked badge={badge} />
                            }
                            <span style={{
                              ...badgeCardName,
                              color: isEarned ? theme.colors.textPrimary : theme.colors.textMuted,
                            }}>
                              {badge.name}
                            </span>
                            {isEarned && earnedDate && (
                              <span style={badgeCardDate}>
                                {new Date(earnedDate).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric'
                                })}
                              </span>
                            )}
                            {!isEarned && (
                              <span style={badgeCardLockText}>Not yet earned</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── CALENDAR ── */}
        {activeTab === 'calendar' && (
          <div className="calendar-section">
            <ProgressCalendar userId={user.id} />
          </div>
        )}

        {/* ── STREAKS ── */}
        {activeTab === 'streaks' && (
          <div className="streaks-section">
            <StreakTracker userId={user.id} />
          </div>
        )}

        {/* ── JOURNAL ── */}
        {activeTab === 'journal' && (
          <div className="journal-section">
            <DailyJournal userId={user.id} />
          </div>
        )}

      </div>
    </div>
  );
};

// ── Header button styles ──
const headerButtonGroup = {
  display: 'flex',
  alignItems: 'stretch',
  gap: '12px',
  flexWrap: 'nowrap',
  width: '100%'
};

const continueScrollButton = {
  background: theme.colors.gradient,
  border: 'none',
  borderRadius: '8px',
  padding: '0.7143em 1.4286em',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: '600',
  cursor: 'pointer',
  flex: '1 1 0',
  minWidth: 0,
  textAlign: 'center',
};

// ── Inline styles for badge UI ──
const badgeCountStyle = {
  backgroundColor: theme.colors.teal,
  color: theme.colors.bgDark,
  fontSize: '0.625rem',
  fontWeight: '700',
  borderRadius: '10px',
  padding: '0.1em 0.6em',
  marginLeft: '4px',
};

const recentBadgesSection = {
  backgroundColor: theme.colors.bgMedium,
  borderRadius: '12px',
  padding: '20px 24px',
  border: `1px solid ${theme.colors.bgLight}`,
  marginBottom: '20px',
};

const recentBadgesTitle = {
  color: theme.colors.textPrimary,
  fontSize: '1rem',
  fontWeight: '600',
  margin: '0 0 16px 0',
};

const recentBadgesRow = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
};

const recentBadgeItem = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  cursor: 'default',
};

const recentBadgeName = {
  color: theme.colors.textSecondary,
  fontSize: '0.625rem',
  textAlign: 'center',
  maxWidth: '64px',
  lineHeight: '1.3',
};

const seeAllButton = {
  backgroundColor: 'transparent',
  border: `1px solid ${theme.colors.bgLight}`,
  borderRadius: '8px',
  padding: '0.6667em 1.1667em',
  color: theme.colors.teal,
  fontSize: '0.75rem',
  fontWeight: '600',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const badgesTab = {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
};

const badgesSummary = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const badgesSummaryText = {
  color: theme.colors.textSecondary,
  fontSize: '0.875rem',
  fontWeight: '500',
};

const badgesProgressBar = {
  height: '6px',
  backgroundColor: theme.colors.bgLight,
  borderRadius: '3px',
  overflow: 'hidden',
};

const badgesProgressFill = {
  height: '100%',
  background: theme.colors.gradient,
  borderRadius: '3px',
  transition: 'width 0.5s ease',
};

const categorySection = {
  backgroundColor: theme.colors.bgMedium,
  borderRadius: '16px',
  padding: '24px',
  border: `1px solid ${theme.colors.bgLight}`,
};

const categoryHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  marginBottom: '20px',
};

const categoryTitle = {
  color: theme.colors.textPrimary,
  fontSize: '1.125rem',
  fontWeight: '700',
  margin: '0 0 4px 0',
};

const categoryDesc = {
  color: theme.colors.textSecondary,
  fontSize: '0.8125rem',
  margin: 0,
};

const categoryCount = {
  backgroundColor: theme.colors.bgLight,
  color: theme.colors.textSecondary,
  fontSize: '0.75rem',
  fontWeight: '700',
  padding: '4px 10px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const badgeGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100px, 45%), 1fr))',
  gap: '12px',
};

const badgeCard = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '16px 8px',
  borderRadius: '12px',
  border: `1px solid ${theme.colors.bgLight}`,
  transition: 'border-color 0.2s',
};

const badgeCardEarned = (color) => ({
  backgroundColor: `${color}08`,
  border: `1px solid ${color}40`,
});

const badgeCardLocked = {
  backgroundColor: theme.colors.bgDark,
  opacity: 0.6,
};

const badgeCardName = {
  fontSize: '0.6875rem',
  fontWeight: '600',
  textAlign: 'center',
  lineHeight: '1.3',
};

const badgeCardDate = {
  color: theme.colors.teal,
  fontSize: '0.625rem',
  textAlign: 'center',
};

const badgeCardLockText = {
  color: theme.colors.textMuted,
  fontSize: '0.625rem',
  textAlign: 'center',
  fontStyle: 'italic',
};

export default ProgressDashboard;
// v2